/**
 * Shared User-Picker-Component fuer alle Module die einen User auswaehlen
 * lassen (Todos-Assign, Bauberichte-Task-Zuweisen, ...). Konsumiert
 * /api/users/search.
 *
 * Verhalten:
 *   - Im „leer"-Zustand: Input fuer Suche, Dropdown mit Treffern beim Tippen.
 *   - Im „gewaehlt"-Zustand: kompakter Chip mit Namen + X-Button zum Aufheben.
 *
 * Phase 1: Suche ueber alle aktiven User (Self optional ausgeschlossen).
 * Adressbuch-Visibility-Filter ist Punkt J im Plan-File und kommt in einer
 * spaeteren Phase ohne Aenderung der Component-API.
 */
import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { api } from '@onvice/portal-shell-api';

interface UserMatch {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
}

export interface UserPickerProps {
  /** Aktuell gewaehlte User-ID (oder null = nichts gewaehlt). */
  value: string | null;
  /** Anzeige-Label des aktuell gewaehlten Users — Caller verwaltet das selbst. */
  label?: string;
  /** Wird beim Auswaehlen aufgerufen: User-ID + Display-Name. Bei Entfernen: (null, ''). */
  onChange: (id: string | null, displayName: string) => void;
  /** Standard true — eigenen User aus den Treffern ausschliessen. */
  excludeSelf?: boolean;
  /** Eingabe-Placeholder. */
  placeholder?: string;
  /** Tailwind-Class fuer den Input. Defaults entsprechen unseren Form-Inputs. */
  inputClassName?: string;
}

const DEFAULT_INPUT_CLASS =
  'w-full h-11 px-3 rounded-md border border-default bg-background text-foreground text-base placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30';

export default function UserPicker({
  value,
  label = '',
  onChange,
  excludeSelf = true,
  placeholder = 'Name oder E-Mail tippen...',
  inputClassName = DEFAULT_INPUT_CLASS,
}: UserPickerProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<UserMatch[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (value) return; // im gewaehlten Zustand keine Suche
    if (!open) return;
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (query) params.set('q', query);
        if (excludeSelf) params.set('exclude_self', '1');
        const r = await api.get<{ success: boolean; users: UserMatch[] }>(
          `/users/search?${params.toString()}`,
        );
        setResults(r.users ?? []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [query, open, value, excludeSelf]);

  if (value) {
    return (
      <div className="flex items-center gap-2 px-3 h-11 rounded-md border border-default bg-background text-foreground">
        <span className="flex-1 truncate">{label || value}</span>
        <button
          type="button"
          onClick={() => onChange(null, '')}
          className="text-muted-foreground hover:text-danger"
          title="Entfernen"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}  // Klick auf Dropdown nicht verschlucken
        placeholder={placeholder}
        className={inputClassName}
      />
      {open && (results.length > 0 || loading) && (
        <div className="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto rounded-md border border-default bg-card shadow-lg">
          {loading && (
            <div className="px-3 py-2 text-sm text-muted-foreground">Suche...</div>
          )}
          {results.map((u) => {
            const name = `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email;
            return (
              <button
                key={u.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}  // verhindert dass onBlur vor onClick feuert
                onClick={() => {
                  onChange(u.id, name);
                  setOpen(false);
                  setQuery('');
                }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-primary/10"
              >
                <div className="font-medium">{name}</div>
                <div className="text-xs text-muted-foreground">{u.email}</div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
